import BaseTextField from '@/components/form/BaseTextField';
import { ComponentProps, FocusEventHandler, Ref, useCallback } from 'react';
import { useScannerTarget } from '../features/register-device/hooks/useScannerTarget';

type BaseTextFieldProps = ComponentProps<typeof BaseTextField>;

type ScannerBaseTextFieldProps = BaseTextFieldProps & {
    inputRef?: Ref<HTMLInputElement>;
    transformScannerValue?: (value: string) => string;
    onScannerValue?: (value: string) => void | Promise<void>;
};

const ScannerBaseTextField = ({
    inputRef,
    transformScannerValue = (value) => value.trim(),
    onScannerValue,
    onFocus,
    onBlur,
    disabled,
    ...props
}: ScannerBaseTextFieldProps) => {
    const handleScannerScan = useCallback(
        async (barcode: string) => {
            if (!onScannerValue) return;

            const nextValue = transformScannerValue(barcode);

            if (!nextValue) return;

            await onScannerValue(nextValue);
        },
        [onScannerValue, transformScannerValue],
    );

    const { activateScannerTarget, deactivateScannerTarget } = useScannerTarget({
        enabled: !disabled && Boolean(onScannerValue),
        onScan: handleScannerScan,
    });

    const handleFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
        activateScannerTarget();
        onFocus?.(event);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
        deactivateScannerTarget();
        onBlur?.(event);
    };

    return (
        <BaseTextField inputRef={inputRef} {...props} disabled={disabled} onFocus={handleFocus} onBlur={handleBlur} />
    );
};

export default ScannerBaseTextField;
